import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { getComputedPropertiesForNet } from "../../../document";
import { filterTruthy } from "../../../helpers";
function parseNumberValue(value) {
    if (typeof value === "number")
        return value;
    const stringParsed = Number(value);
    if (typeof value === "string" && !isNaN(stringParsed))
        return stringParsed;
    return undefined;
}
function parseStringValue(value) {
    if (typeof value === "string")
        return value;
    return undefined;
}
export function parseElementInheritedProps(props) {
    const currentProperties = Object.values(props).find((p) => p[0]?.name === defaultPropertyDefinitions.current?.label);
    const desiredTemperatureRiseProperties = Object.values(props).find((p) => p[0]?.name === defaultPropertyDefinitions.desiredTemperatureRise?.label);
    const controlledImpedanceProperties = Object.values(props).find((p) => p[0]?.name === defaultPropertyDefinitions.controlled_impedance?.label);
    const controlledImpedancePairProperties = Object.values(props).find((p) => p[0]?.name === defaultPropertyDefinitions.controlled_impedance_pair?.label);
    const busGroupProperties = Object.values(props).find((p) => p[0]?.name === defaultPropertyDefinitions.bus_group?.label);
    const current = currentProperties && filterTruthy(currentProperties.map((v) => parseNumberValue(v?.value)));
    const desiredTemperatureRise = desiredTemperatureRiseProperties &&
        filterTruthy(desiredTemperatureRiseProperties.map((v) => parseNumberValue(v?.value)));
    const controlledImpedance = controlledImpedanceProperties &&
        filterTruthy(controlledImpedanceProperties.map((v) => parseNumberValue(v?.value)));
    const controlledImpedancePair = controlledImpedancePairProperties &&
        filterTruthy(controlledImpedancePairProperties.map((v) => ({
            value: parseStringValue(v?.value),
            parentUid: v?.parentUid,
        })));
    const busGroup = busGroupProperties && filterTruthy(busGroupProperties.map((v) => parseStringValue(v?.value)));
    return {
        current,
        desiredTemperatureRise,
        controlledImpedance,
        controlledImpedancePair,
        busGroup,
    };
}
export function mergeInheritedProps(net, elements) {
    const properties = getComputedPropertiesForNet(net, elements);
    const parsedProps = parseElementInheritedProps(properties);
    return {
        // Impedance: We need a single value and we need it to be stable, so we pick the biggest one (arbitrarily?)
        controlledImpedance: parsedProps.controlledImpedance?.sort((a, b) => b - a)[0],
        // Others: We can have different values and we need all of them to match correctly, so we return an array
        controlledImpedancePair: parsedProps.controlledImpedancePair,
        busGroup: parsedProps.busGroup,
        // Current: We need a single value and we need it to be stable, so we intentionally pick the biggest one
        current: parsedProps.current?.sort((a, b) => b - a)[0],
        desiredTemperatureRise: parsedProps.desiredTemperatureRise?.sort((a, b) => b - a)[0],
        // We also need the uid to be able to match nets
        uid: net.uid,
    };
}
// Given two nets, should they be part of the same balanced pair?
export function areNetsImpedancePaired(netA, netB) {
    if (netB.uid === netA.uid)
        return false;
    const pairPropEmpty = netA.controlledImpedancePair === undefined || netB.controlledImpedancePair === undefined;
    // No match if either pair properties are empty
    const pairPropMatch = !pairPropEmpty &&
        netB.controlledImpedancePair?.some((p) => 
        // Note this should also match on undefined parentUids values as we don't have a parentUid for the net properties
        netA.controlledImpedancePair?.some((q) => p.value === q.value && p.parentUid === q.parentUid));
    // If one is empty, then match
    const busGroupPropEmpty = netA.busGroup === undefined || netB.busGroup === undefined;
    const busGroupPropMatch = busGroupPropEmpty || netB.busGroup?.some((p) => netA.busGroup?.includes(p));
    return pairPropMatch && busGroupPropMatch;
}
export function getInheritedPropValues(netWithInfo, netsWithImpedanceProps) {
    let impedancePair;
    let impedance;
    let current;
    let desiredTemperatureRise;
    if (netWithInfo.controlledImpedance !== undefined && netWithInfo.controlledImpedancePair !== undefined) {
        impedance = `${netWithInfo.controlledImpedance}`;
        const otherNets = netsWithImpedanceProps.filter((n) => areNetsImpedancePaired(netWithInfo, n));
        // We have multiple options, but we default to the first one
        const otherNet = otherNets.sort()[0];
        if (otherNet) {
            impedancePair = otherNet.uid;
        }
    }
    else if (netWithInfo.controlledImpedance !== undefined) {
        impedance = `${netWithInfo.controlledImpedance}`;
    }
    else if (netWithInfo.current !== undefined) {
        current = `${netWithInfo.current}`;
        if (netWithInfo.desiredTemperatureRise) {
            desiredTemperatureRise = `${netWithInfo.desiredTemperatureRise}`;
        }
    }
    return { impedance, impedancePair, current, desiredTemperatureRise };
}
//# sourceMappingURL=propertyInheritance.js.map